import * as React from 'react';
import {
    CREATE_CHANNEL,
    GET_CHANNELS_OVERVIEW_DATA,
    GET_TABLE_OVERVIEW_DATA,
    RE_UPLOAD_FILE,
    RETRY_STEP,
    UPLOAD_FILE,
} from '../queries.ts';
import DocumentOverview, { formatTime, getArchivedInfo } from '../../../document/pages/Overview';
import * as constants from '../constants.ts';
import { DocumentType } from '../../../document/constants.ts';
import { useTranslation } from 'react-i18next';
import { FutureDateFilter } from '../../../document/pages/Overview/internal/DateFilter.tsx';
import {
    convertFromDateTimeFilters,
    convertToDateTimeFilters,
} from '../../../document/pages/Overview/internal/FilterBar.tsx';
import { differenceInDays, startOfDay } from 'date-fns/fp';
import classnames from '../../../core/utils/classnames.tsx';

const SubmissionDateCell = (record: any) => {
    const { t } = useTranslation('assistance');

    if (!record?.submissionDate) {
        return <span className="text-content-weak">{t('overview.columns.notFound')}</span>;
    }

    const isArchived = !!getArchivedInfo(record)?.archivedAt;

    const start = startOfDay(new Date());
    const end = startOfDay(new Date(record.submissionDate));

    const daysDifference = differenceInDays(start, end) || 0;

    return (
        <div className="flex flex-col space-y-1">
            <div>{formatTime(record.submissionDate)}</div>
            {daysDifference <= 7 && !isArchived && (
                <div
                    className={classnames(
                        'text-xs',
                        daysDifference >= 0
                            ? 'bg-warning-100 text-content-warning-strong'
                            : 'bg-danger-100 text-content-danger-strong'
                    )}
                >
                    {daysDifference >= 0
                        ? t('overview.columns.submissionDateDue', { count: daysDifference + 1 })
                        : t('overview.columns.submissionDateOverdue')}
                </div>
            )}
        </div>
    );
};

const ExtraFilters = ({ filters, onFiltersChange }) => {
    return (
        <div className="flex-nowrap">
            <FutureDateFilter
                filters={convertFromDateTimeFilters(filters)}
                onFiltersChange={(filters) => onFiltersChange(convertToDateTimeFilters(filters))}
                filterName="submission_date"
            />
        </div>
    );
};

const Overview = (props) => {
    const { t } = useTranslation('assistance');
    const documentConfiguration = {
        documentType: DocumentType.ListOfServices,
        documentTypeName: 'listOfServices',
        activeTab: 'list-of-services',
        constants: constants,
        CREATE_CHANNEL: CREATE_CHANNEL,
        GET_CHANNELS_OVERVIEW_DATA: GET_CHANNELS_OVERVIEW_DATA,
        GET_TABLE_OVERVIEW_DATA: GET_TABLE_OVERVIEW_DATA,
        RETRY_STEP: RETRY_STEP,
        UPLOAD_FILE: UPLOAD_FILE,
        RE_UPLOAD_FILE: RE_UPLOAD_FILE,
        extraColumns: [
            {
                key: 'submissionDate',
                label: t('overview.columns.submissionDate'),
                render: SubmissionDateCell,
                className: 'min-w-60 w-60',
            },
        ],
        orderByOptions: [
            {
                value: 'created_at',
                label: t('overview.filters.orderBy.options.createdAt'),
                isDate: true,
            },
            {
                value: 'created_by__email',
                label: t('overview.filters.orderBy.options.createdBy'),
            },
            {
                value: 'assigned_to__email',
                label: t('overview.filters.orderBy.options.assignedTo'),
            },
            {
                value: 'submission_date',
                label: t('overview.filters.orderBy.options.submissionDate'),
                ascLabel: t('overview.filters.orderBy.options.ascendingSubmissionDate'),
                descLabel: t('overview.filters.orderBy.options.descendingSubmissionDate'),
                isDate: true,
            },
        ],
        renderExtraFilters: ExtraFilters,
    };

    return <DocumentOverview documentConfiguration={documentConfiguration} {...props} />;
};

export default Overview;
